<template>
    <div class="flex items-center justify-center">
        <template v-if="canViewChat">
            <BaseButton
                v-for="plugin in shownReaderPlugins"
                :key="`plugin-button-mobile-${plugin.key}`"
                :tooltip="plugin.title"
                :class="[isReaderPluginOpen(plugin.key) ? 'header-btn-active' : 'text-theme-icon']"
                :disabled="isOffline"
                :icon="plugin.icon"
                :aria-expanded="String(isReaderPluginOpen(plugin.key))"
                :data-reader-plugin-button="plugin.key"
                :dusk="`buttonPlugin-${plugin.key}`"
                @click="togglePlugin(plugin.key)"
            />
        </template>

        <BaseButton
            v-if="canUseListening"
            :tooltip="localizations[lang].tooltips.listen"
            :class="[listeningIsActive ? 'header-btn-active' : 'text-theme-icon', 'block']"
            :disabled="isOffline"
            icon="baseline-headset-24px"
            @click="listeningPage"
        />

        <BaseButton
            v-if="canViewFullscreen"
            key="fullscreen-button"
            dusk="toggleFullscreen"
            :tooltip="
                isFullscreen
                    ? localizations[lang].tooltips.closeFullscreen
                    : localizations[lang].tooltips.openFullscreen
            "
            :class="[isFullscreen ? 'header-btn-active' : 'text-theme-icon']"
            :icon="isFullscreen ? 'arrows-pointing-in-24px' : 'arrows-pointing-out-24px'"
            :aria-pressed="String(isFullscreen)"
            @click="toggleFullscreen"
        />

        <BaseButton
            v-if="canViewSetting"
            key="settings-button"
            :tooltip="localizations[lang].tooltips.config"
            dusk="toggleSettings"
            :class="[ui.settings.opened ? 'header-btn-active' : 'text-theme-icon', 'block md:relative']"
            icon="display-setting"
            :aria-expanded="String(ui.settings.opened)"
            @click="toggleSettings"
        />

        <div class="relative">
            <BaseButton
                v-if="canUseSearch"
                :tooltip="localizations[lang].tooltips.search"
                :class="[ui.search.opened ? 'header-btn-active' : 'text-theme-icon']"
                dusk="toggleSearch"
                :disabled="isOffline"
                icon="magnifying-glass"
                :aria-expanded="String(ui.search.opened)"
                @click="toggleSearch"
            />
            <MenuSearch />
        </div>

        <BaseButton
            v-if="canViewBookmark"
            key="bookmark-button"
            dusk="toggleBookmark"
            :tooltip="localizations[lang].tooltips.bookmarks"
            :class="[isCurrentPageBookmarked ? 'header-btn-active' : 'text-theme-icon']"
            :disabled="isOfflineOnRemoteContent"
            :icon="isCurrentPageBookmarked ? 'baseline-bookmark-24px' : 'baseline-bookmark_border-24px'"
            :aria-pressed="String(isCurrentPageBookmarked)"
            @click="toggleBookmark"
        />

        <BaseButton
            v-if="canViewNotebooks"
            key="notebook-button-mobile"
            :tooltip="localizations[lang].notebook"
            :class="[ui.notebook.opened ? 'header-btn-active' : 'text-theme-icon']"
            :disabled="isOfflineOnRemoteContent"
            icon="outline-description-24px"
            :aria-expanded="String(ui.notebook.opened)"
            @click="toggleNotebook"
        />
        <BaseButton
            v-if="canViewFlashcards"
            key="flashcards-button-mobile"
            :tooltip="localizations[lang].flashcards"
            :class="[ui.flashcardsPanel.opened ? 'header-btn-active' : 'text-theme-icon']"
            :disabled="isOfflineOnRemoteContent"
            icon="flashcard-24px"
            :aria-expanded="String(ui.flashcardsPanel.opened)"
            @click="toggleFlashcardsPanel"
        />
        <BaseButton
            v-if="canViewAddOns"
            key="add-ons-button-mobile"
            :tooltip="addOnsTitle"
            :class="[ui.addOnsPanel.opened ? 'header-btn-active' : 'text-theme-icon']"
            icon="squares-plus-24px"
            dusk="buttonAddOns"
            :aria-expanded="String(ui.addOnsPanel.opened)"
            @click="toggleAddOnsPanel"
        />
        <MenuMore
            v-if="!isOpenFullViewContentModal"
            :is-menu-visible="ui.rightSubmenu.opened"
            @toggle="toggleRightSubmenu"
        />
    </div>
</template>

<script>
'use strict';
import { mapActions, mapGetters, mapState } from 'vuex';
import BaseButton from '../Components/buttons/BaseButton.vue';
import MenuMore from '../Components/MenuMore.vue';
import MenuSearch from '../Components/MenuSearch.vue';
import { addOnStrings } from '../AddOns/addOnStrings.js';

export default {
    components: {
        MenuMore,
        BaseButton,
        MenuSearch,
    },
    computed: {
        ...mapState(['ui', 'reader', 'isOffline', 'localizations', 'lang', 'isFullscreen']),
        ...mapGetters([
            'onlyDesktopVisible',
            'onlyMobileVisible',
            'canUseReaderPlugins',
            'shownReaderPlugins',
            'isReaderPluginOpen',
            'canUseFlashcards',
            'canUseFullscreen',
            'isOfflineOnRemoteContent',
            'canUseBookmarks',
            'isCurrentPageBookmarked',
            'bookmarks',
            'canUseAddOns',
        ]),
        addOnsTitle() {
            return addOnStrings(this.localizations, this.lang).title;
        },
        listeningIsActive() {
            return this.ui.listening.opened && this.ui.listening.texToListen === this.ui.listening.pageToListen;
        },
        canViewChat() {
            return this.onlyMobileVisible && this.canUseReaderPlugins && !this.isOpenFullViewContentModal;
        },
        canUseListening() {
            return (
                this.onlyDesktopVisible &&
                this.ui.listening.enabled &&
                this.ui.listening.pageToListen &&
                !this.isOpenFullViewContentModal
            );
        },
        canViewSetting() {
            return this.onlyDesktopVisible && !this.isOpenFullViewContentModal;
        },
        canViewFullscreen() {
            return this.canUseFullscreen && !this.isOpenFullViewContentModal;
        },
        canUseSearch() {
            return this.ui.search.enabled && !this.isOpenFullViewContentModal;
        },
        canViewNotebooks() {
            return this.onlyMobileVisible && !this.isOpenFullViewContentModal;
        },
        canViewBookmark() {
            return this.canUseBookmarks && !this.isOpenFullViewContentModal;
        },
        canViewFlashcards() {
            return this.onlyMobileVisible && !this.isOpenFullViewContentModal && this.canUseFlashcards;
        },
        canViewAddOns() {
            return this.onlyMobileVisible && !this.isOpenFullViewContentModal && this.canUseAddOns;
        },
        isOpenFullViewContentModal() {
            if (!this.onlyMobileVisible) {
                return false;
            }
            return (
                this.ui.notebook.opened ||
                this.ui.flashcardsPanel.opened ||
                this.ui.addOnsPanel.opened ||
                this.ui.tableOfContents.opened ||
                this.ui.thumbnails.opened ||
                this.ui.search.opened ||
                this.ui.plugin.opened
            );
        },
    },
    watch: {
        // Re-evaluate when bookmarks are added/removed or first loaded (the toggle button
        // and the BookmarkFold both read the committed currentPageBookmark from the store).
        bookmarks() {
            this.refreshBookmarkState();
        },
    },
    mounted() {
        // The active reader (PDF/EPUB) emits 'pageChanged' on every navigation, scroll and
        // relayout/resize; that is the trigger to recompute whether the current page holds a
        // bookmark. We recompute imperatively rather than via a reactive getter because the
        // EPUB visible range lives on the non-reactive navigator instance.
        this.$bus.$on('pageChanged', this.refreshBookmarkState);
        this.refreshBookmarkState();
    },
    beforeDestroy() {
        this.$bus.$off('pageChanged', this.refreshBookmarkState);
    },
    methods: {
        ...mapActions([
            'listeningPage',
            'toggleNotebook',
            'toggleFlashcardsPanel',
            'toggleAddOnsPanel',
            'toggleSearch',
            'toggleSettings',
            'toggleFullscreen',
            'togglePlugin',
            'toggleRightSubmenu',
            'toggleBookmark',
            'refreshCurrentPageBookmark',
        ]),
        refreshBookmarkState() {
            if (this.canUseBookmarks) this.refreshCurrentPageBookmark();
        },
    },
};
</script>
